import React, { useState, useMemo, useCallback, useEffect } from 'react';
import classnames from 'clsx';
import { sortBy } from 'lodash-es';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { NumberInput } from 'ui-components/components/number-input';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import { messages } from './messages';
import InventorySyncWarehouseDropdown from './inventory-sync-warehouse-dropdown';
import { useInventoryWarehouses } from '../../hooks/use-inventory-warehouses';
import { updateInventorySyncConnections } from '../../api/inventory-sync-api';
import { SimpleModal } from '../../../components/simple-modal/simple-modal';
import { FeatureFlagDeclarations } from '../../../feature-flags/feature-flag-declarations';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag';
import { useSimpleToggle } from '../../../hooks/use-simple-toggle';
import { Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import styles from './styles.module.less';
import { emptyObject } from 'ui-components/default-props/empty';

/**
 * @typedef {object} SyncConfigConnection
 * @property {string} connectionId
 * @property {string} terminusType
 * @property {string} [connectionType]
 * @property {string} [externalId]
 * @property {object} config
 * @property {boolean} [config.min_sellable_quantity_enabled]
 * @property {number} [config.min_sellable_quantity]
 * @property {boolean} [config.max_sellable_quantity_enabled]
 * @property {number} [config.max_sellable_quantity]
 * @property {boolean} [config.buffer_stock_quantity_enabled]
 * @property {number} [config.buffer_stock_quantity]
 * @property {boolean} [config.sync_percentage_quantity_enabled]
 * @property {number} [config.sync_percentage_quantity]
 * @property {object} [config.sync_all_to_warehouse]
 * @property {string} [config.sync_all_to_warehouse.marketplace_warehouse_id]
 * @property {boolean} [config.sync_all_to_warehouse.enabled]
 */

/**
 * Modal for configuring inventory sync options
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {string} props.targetName
 * @param {SyncConfigConnection} props.connection
 * @param {(config: object) => void} props.onSaveConfig
 * @param {boolean} [props.readOnly]
 * @returns {React.ReactElement} The sync config modal component
 */
export const SyncConfigModal = ({ onClose, targetName, connection, onSaveConfig, readOnly = false }) => {
  const { formatMessage } = useIntl();
  const trackEditProcess = useTrackWorkflow(Workflows.EDIT_INVENTORY_SYNC_OPTIONS_FLOW);
  const isSyncPercentageFeatureEnabled = useFeatureFlag(FeatureFlagDeclarations.enableInventorySyncPercentage);

  const {
    min_sellable_quantity_enabled: isSetMinimumQuantityEnabled = false,
    max_sellable_quantity_enabled: isSetMaximumQuantityEnabled = false,
    min_sellable_quantity: configuredMinQuantity = 0,
    max_sellable_quantity: configuredMaxQuantity = 0,
    buffer_stock_quantity_enabled: isSetBufferStockQuantityEnabled = false,
    buffer_stock_quantity: configuredBufferStockQuantity = 0,
    sync_percentage_quantity_enabled: isSetSyncPercentageEnabled = false,
    sync_percentage_quantity: configuredSyncPercentage = 100,
    sync_all_to_warehouse: configuredSyncToWarehouse = emptyObject,
  } = connection.config ?? emptyObject;

  const [isMinimumQuantityEnabled, toggleMinimumQuantity] = useSimpleToggle(isSetMinimumQuantityEnabled);
  const [isMaximumQuantityEnabled, toggleMaximumQuantity] = useSimpleToggle(isSetMaximumQuantityEnabled);
  const [isBufferStockQuantityEnabled, toggleBufferStockQuantity] = useSimpleToggle(isSetBufferStockQuantityEnabled);
  const [isSyncPercentageEnabled, toggleSyncPercentage] = useSimpleToggle(isSetSyncPercentageEnabled);
  const [minimumQuantity, setMinimumQuantity] = useState(configuredMinQuantity);
  const [maximumQuantity, setMaximumQuantity] = useState(configuredMaxQuantity);
  const [bufferStockQuantity, setBufferStockQuantity] = useState(configuredBufferStockQuantity);
  const [syncPercentage, setSyncPercentage] = useState(configuredSyncPercentage);
  const [syncToWarehouse, setSyncToWarehouse] = useState(configuredSyncToWarehouse);

  const isSetSyncAllToWarehouseEnabled = connection.config?.sync_all_to_warehouse?.enabled ?? false;
  const [isSyncAllToWarehouseEnabled, toggleSyncAllToWarehouseEnabled] = useSimpleToggle(isSetSyncAllToWarehouseEnabled);
  const [selectedWarehouse, setSelectedWarehouse] = useState({});
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [allowShowErrorBanner, setAllowShowErrorBanner] = useState(false);

  const { fetchInventoryWarehouses, loading, data: warehouses } = useInventoryWarehouses(false);

  useEffect(() => {
    fetchInventoryWarehouses(connection?.externalId);
  }, [fetchInventoryWarehouses, connection?.externalId]);

  const handleCancel = () => {
    trackEditProcess(WorkflowSteps.CANCEL, { cancelClicked: true });
    onClose();
  };

  const handleClose = () => {
    trackEditProcess(WorkflowSteps.CANCEL, { closeClicked: true });
    onClose();
  };

  const showMinMaxErrorHighlighting = () => {
    return hasAttemptedSave && isMaximumQuantityEnabled && minimumQuantity >= maximumQuantity;
  };

  const onSave = useCallback(async () => {
    const hasMinMaxError = () => {
      return isMinimumQuantityEnabled && isMaximumQuantityEnabled && minimumQuantity >= maximumQuantity;
    };

    const handleError = (trackEditData, closeWindow = true, errorMsg = messages.errorSavingMessage) => {
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.errorSavingTitle} />,
        content: <FormattedMessage {...errorMsg} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });

      trackEditProcess(WorkflowSteps.ERROR, trackEditData);

      if (closeWindow) {
        onClose();
      }

      // oxlint-disable-next-line no-param-reassign
      trackEditData.error = undefined;
    };

    /*
        Desired min/max error handling behavior:
        - On initial load, no error banner should be shown.
        - If the user has not attempted to save, no error banner or error highlighting should be shown.
        - If the user has attempted to save and there is a min/max error, the error banner and error highlighting should be shown.
        - If the user has attempted to save and there is no min/max error, the error banner and error highlighting should not be shown.
        - If the user has attempted to save and there is a min/max error, but the user has since fixed the error,
            the error banner SHOULD CONTINUE TO SHOW, BUT error highlighting should not be shown.
    */
    setHasAttemptedSave(true);

    if (hasMinMaxError()) {
      setAllowShowErrorBanner(true);
    }

    const updateConnectionRequest = {
      connectionId: connection.connectionId,
      terminusType: connection.terminusType,
      config: {
        ...connection.config,
        min_sellable_quantity_enabled: isMinimumQuantityEnabled,
        max_sellable_quantity_enabled: isMaximumQuantityEnabled,
        buffer_stock_quantity_enabled: isBufferStockQuantityEnabled,
        sync_percentage_quantity_enabled: isSyncPercentageEnabled,
        sync_all_to_warehouse: {
          ...syncToWarehouse,
          enabled: isSyncAllToWarehouseEnabled,
        },
      },
    };

    const trackEditData = {
      connectionConfig: { ...updateConnectionRequest.config },
      updateConnectionRequest,
    };

    try {
      trackEditProcess(WorkflowSteps.START);

      if (maximumQuantity !== undefined) {
        updateConnectionRequest.config.max_sellable_quantity = maximumQuantity;
      }

      if (minimumQuantity !== undefined) {
        updateConnectionRequest.config.min_sellable_quantity = minimumQuantity;
      }

      if (bufferStockQuantity !== undefined) {
        updateConnectionRequest.config.buffer_stock_quantity = bufferStockQuantity;
      }

      if (syncPercentage !== undefined) {
        updateConnectionRequest.config.sync_percentage_quantity = syncPercentage;
      }

      trackEditData.updateConnectionRequest = updateConnectionRequest;

      if (isMaximumQuantityEnabled && isMinimumQuantityEnabled && maximumQuantity <= minimumQuantity) {
        trackEditData.error = messages.errorMaxMustBeGreaterThanMinQty.defaultMessage;
        handleError(trackEditData, false, messages.errorMaxMustBeGreaterThanMinQty);
        return;
      }

      await updateInventorySyncConnections([updateConnectionRequest]);

      // Update the in memory connection object with the new values
      trackEditData.connectionConfig = updateConnectionRequest.config;

      NotificationService.showNotification({
        title: <FormattedMessage {...messages.successSavingTitle} />,
        content: <FormattedMessage {...messages.successSavingMessage} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });

      trackEditProcess(WorkflowSteps.COMPLETE, trackEditData);
      onSaveConfig(updateConnectionRequest.config);
      onClose();
    } catch (error) {
      trackEditData.updateConnectionRequest = updateConnectionRequest;
      trackEditData.connectionConfig = { ...connection.config };
      trackEditData.error = error;
      handleError(trackEditData);
    }
  }, [
    connection.config,
    isMaximumQuantityEnabled,
    isMinimumQuantityEnabled,
    maximumQuantity,
    minimumQuantity,
    isBufferStockQuantityEnabled,
    bufferStockQuantity,
    isSyncPercentageEnabled,
    syncPercentage,
    onClose,
    trackEditProcess,
    connection.connectionId,
    connection.terminusType,
    onSaveConfig,
    syncToWarehouse,
    isSyncAllToWarehouseEnabled,
  ]);

  useEffect(() => {
    const items = warehouses ?? [];
    let warehouseToSelect = items.find(
      (i) => i.key === selectedWarehouse.marketplace_warehouse_id || i.key === syncToWarehouse?.marketplace_warehouse_id,
    );

    if (!warehouseToSelect) {
      warehouseToSelect = sortBy(items, 'key')[0];
    }

    if (warehouseToSelect) {
      setSelectedWarehouse(warehouseToSelect);
    }
  }, [warehouses, selectedWarehouse.marketplace_warehouse_id, syncToWarehouse.marketplace_warehouse_id]);

  const getToggleLabel = useMemo(() => {
    if (loading) {
      return 'Loading...';
    }

    if (selectedWarehouse?.text) {
      return selectedWarehouse.text;
    }

    return undefined;
  }, [selectedWarehouse, loading]);

  const shouldShowMapWarehouseSection = useMemo(() => {
    const allowedMapWarehouseConnectionTypes = ['shopify'];
    return allowedMapWarehouseConnectionTypes.includes(connection?.connectionType?.toLowerCase());
  }, [connection?.connectionType]);

  const hasWarehouses = useMemo(() => {
    return loading || warehouses?.length > 1;
  }, [warehouses, loading]);

  return (
    <SimpleModal
      actionMessage={messages.saveModal}
      cancelMessage={messages.closeModal}
      headerMessage={formatMessage(messages.modalHeader, { targetName })}
      isActionDisabled={readOnly || showMinMaxErrorHighlighting()}
      onAction={onSave}
      onCancel={handleCancel}
      onClose={handleClose} // This doesn't seem to be called, but throws a nasty console error if not included since it is required.
      size='medium'
    >
      <FlexColumn className={styles['modal-content']}>
        <BannerMessageV2
          content={<Text textType={TextType.Caption} value={messages.minMaxNextAffectMessage} />}
          layout={BannerLayout.Default}
          status={BannerStatus.Info}
        />
        <div>
          {allowShowErrorBanner === true && (
            <FlexRow className={styles['modal-content']}>
              <BannerMessageV2
                content={
                  <Text
                    textType={TextType.Caption}
                    value={<FormattedMessage {...messages.maxQuantityMustBeGreaterThanMinimumQuantity} />}
                  />
                }
                layout={BannerLayout.Inline}
                status={BannerStatus.Error}
              />
            </FlexRow>
          )}
          <FlexRow className={styles['modal-content']}>
            <FlexColumn className={styles['slider-column']}>
              <CheckboxSlider
                checkboxId='set-minimum-quantity-checkbox'
                checked={isMinimumQuantityEnabled}
                className={styles.slider}
                labelText={<FormattedMessage {...messages.setMinimumQuantity} />}
                onChange={toggleMinimumQuantity}
                testid='set-minimum-quantity-checkbox'
              />
              <Text className={styles['input-info']} textType={TextType.Paragraph} value={messages.minimumQuantityInfo} />
            </FlexColumn>

            <FlexColumn className={styles['input-column']}>
              <FlexRow className={styles['input-setter-row']}>
                <NumberInput
                  className={classnames(styles['input-setter'], {
                    [styles['input-setter-error']]: showMinMaxErrorHighlighting(),
                  })}
                  disabled={!isMinimumQuantityEnabled}
                  label={formatMessage(messages.units)}
                  min={0}
                  onChange={setMinimumQuantity}
                  testId='set-minimum-quantity-input'
                  value={minimumQuantity}
                />
                <Text
                  className={classnames({
                    [styles['input-label-error']]: showMinMaxErrorHighlighting(),
                  })}
                  textType={TextType.Label}
                  value={messages.units}
                />
              </FlexRow>
            </FlexColumn>
          </FlexRow>
        </div>
        <div>
          <FlexRow className={styles['modal-content']}>
            <FlexColumn className={styles['slider-column']}>
              <CheckboxSlider
                checkboxId='set-maximum-quantity-checkbox'
                checked={isMaximumQuantityEnabled}
                className={styles.slider}
                labelText={<FormattedMessage {...messages.setMaxiumQuantity} />}
                onChange={toggleMaximumQuantity}
                testId='set-maximum-quantity-checkbox'
              />
              <Text className={styles['input-info']} textType={TextType.Paragraph} value={messages.maximumQuantityInfo} />
            </FlexColumn>

            <FlexColumn className={styles['input-column']}>
              <FlexRow className={styles['input-setter-row']}>
                <NumberInput
                  className={classnames(styles['input-setter'], {
                    [styles['input-setter-error']]: showMinMaxErrorHighlighting(),
                  })}
                  disabled={!isMaximumQuantityEnabled}
                  label={formatMessage(messages.units)}
                  min={0}
                  onChange={setMaximumQuantity}
                  testId='set-maximum-quantity-input'
                  value={maximumQuantity}
                />
                <Text
                  className={classnames({
                    [styles['input-label-error']]: showMinMaxErrorHighlighting(),
                  })}
                  textType={TextType.Label}
                  value={messages.units}
                />
              </FlexRow>
            </FlexColumn>
          </FlexRow>
        </div>
        <div>
          <FlexRow className={styles['modal-content']}>
            <FlexColumn className={styles['slider-column']}>
              <CheckboxSlider
                checkboxId='set-buffer-stock-quantity-checkbox'
                checked={isBufferStockQuantityEnabled}
                className={styles.slider}
                labelText={<FormattedMessage {...messages.setBufferStockQuantity} />}
                onChange={toggleBufferStockQuantity}
                testId='set-buffer-stock-quantity-checkbox'
              />
              <Text className={styles['input-info']} textType={TextType.Paragraph} value={messages.bufferStockQuantityInfo} />
            </FlexColumn>

            <FlexColumn className={styles['input-column']}>
              <FlexRow className={styles['input-setter-row']}>
                <NumberInput
                  className={styles['input-setter']}
                  disabled={!isBufferStockQuantityEnabled}
                  label={formatMessage(messages.units)}
                  min={0}
                  onChange={setBufferStockQuantity}
                  testId='set-buffer-stock-quantity-input'
                  value={bufferStockQuantity}
                />
                <Text textType={TextType.Label} value={messages.units} />
              </FlexRow>
            </FlexColumn>
          </FlexRow>
        </div>
        {isSyncPercentageFeatureEnabled ? (
          <div>
            <FlexRow className={styles['modal-content']}>
              <FlexColumn className={styles['slider-column']}>
                <CheckboxSlider
                  checkboxId='set-sync-percentage-checkbox'
                  checked={isSyncPercentageEnabled}
                  className={styles.slider}
                  labelText={<FormattedMessage {...messages.setSyncPercentage} />}
                  onChange={toggleSyncPercentage}
                  testId='set-sync-percentage-checkbox'
                />
                <Text className={styles['input-info']} textType={TextType.Paragraph} value={messages.syncPercentageInfo} />
              </FlexColumn>

              <FlexColumn className={styles['input-column']}>
                <FlexRow className={styles['input-setter-row']}>
                  <NumberInput
                    className={styles['input-setter']}
                    disabled={!isSyncPercentageEnabled}
                    label={formatMessage(messages.percentage)}
                    max={100}
                    min={0}
                    onChange={setSyncPercentage}
                    testId='set-sync-percentage-input'
                    value={syncPercentage}
                  />
                  %
                </FlexRow>
              </FlexColumn>
            </FlexRow>
          </div>
        ) : null}
        <div>{shouldShowMapWarehouseSection ? <hr className={styles['min-max-hr']} /> : null}</div>
        {shouldShowMapWarehouseSection ? (
          <div>
            {/*
             * TODO: Test with 2 sources and 2 targets, making sure that the correct source warehouses are displayed and correct map gets updated.
             */}
            <FlexRow className={styles['setter-wrapper']}>
              <Text textType={TextType.H3} value={messages.mapWarehousesSectionHeader} />
              <Text
                className={styles['input-info']}
                textType={TextType.Paragraph}
                value={<FormattedMessage {...messages.mapWarehousesInfo} values={{ channel: targetName }} />}
              />
            </FlexRow>
            <FlexRow className={styles['modal-content']}>
              <FlexColumn className={styles['slider-column']}>
                <CheckboxSlider
                  checkboxId='set-sync-all-to-warehouse-checkbox'
                  checked={isSyncAllToWarehouseEnabled}
                  className={styles.slider}
                  disabled={!hasWarehouses}
                  labelText={<FormattedMessage {...messages.mapWarehousesToSingleWarehouse} />}
                  onChange={toggleSyncAllToWarehouseEnabled}
                  testid='set-sync-all-to-warehouse-checkbox'
                />
              </FlexColumn>
              <FlexColumn className={styles['input-column']}>
                <FlexRow className={styles['input-setter-row']}>
                  <InventorySyncWarehouseDropdown
                    disabled={!isSyncAllToWarehouseEnabled || !hasWarehouses}
                    items={warehouses}
                    loading={loading}
                    onItemClicked={(warehouseItem) => {
                      if (warehouseItem?.key) {
                        setSelectedWarehouse(warehouseItem);
                        setSyncToWarehouse({
                          marketplace_warehouse_id: warehouseItem.key,
                          enabled: isSyncAllToWarehouseEnabled,
                        });
                      }
                    }}
                    selectedItem={selectedWarehouse?.key}
                    toggleLabel={getToggleLabel || <FormattedMessage {...messages.mapWarehousesSelectWarehouse} />}
                  />
                </FlexRow>
              </FlexColumn>
            </FlexRow>
            {!hasWarehouses && (
              <FlexRow className={styles['modal-content']}>
                <FlexColumn className={styles['checkbox-column']} />
                <FlexColumn className={styles['warehouse-dropdown-column']}>
                  <BannerMessageV2
                    content={
                      <Text
                        textType={TextType.Caption}
                        value={<FormattedMessage {...messages.mapWarehousesAddWarehousesToChannel} values={{ channel: targetName }} />}
                      />
                    }
                    layout={BannerLayout.Inline}
                    status={BannerStatus.Warning}
                  />
                </FlexColumn>
              </FlexRow>
            )}
          </div>
        ) : null}
      </FlexColumn>
    </SimpleModal>
  );
};
